import { useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { MfaEnrolment, MfaRequirement, ReadUser } from '@tinct/core-api-models';
import { useGetUserMfaStatus, useStartMfaEnrolment } from '@tinct/core-api';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@tinct/web-components';
import { IconShieldCheck, IconShieldOff } from '@tabler/icons-react';
import { LockIcon, TriangleAlertIcon } from 'lucide-react';
import { EnableMfaModal } from './mfa/enable-mfa-modal';
import { DisableMfaModal } from './mfa/disable-mfa-modal';
import { RegenerateRecoveryCodesModal } from './mfa/regenerate-recovery-codes-modal';

/** Below this many unused codes we nudge the user to regenerate before they run out. */
const LOW_RECOVERY_CODES = 3;

/** Why the second factor is required (B-260): an administrator account, or the user's workspaces. */
const describeRequirement = (requiredBy: MfaRequirement | null | undefined): string => {
  const workspaces = (requiredBy?.organizations ?? [])
    .map((organization) => organization.name)
    .filter((name): name is string => !!name);
  if (requiredBy?.superAdmin) {
    return 'Required for administrator accounts.';
  }
  return workspaces.length > 0
    ? `Required by ${workspaces.join(', ')}.`
    : 'Required by your workspace.';
};

type TwoFactorSectionProps = {
  user: ReadUser;
};

type OpenDialog = 'enable' | 'disable' | 'regenerate' | null;

export const TwoFactorSection = ({ user }: TwoFactorSectionProps) => {
  const userId = user.id as string;
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const [enrolment, setEnrolment] = useState<MfaEnrolment | null>(null);

  const { data: status, isLoading } = useGetUserMfaStatus({ userId });

  // The secret is returned once, so it is fetched by the button and handed to the dialog.
  const startEnrolment = useStartMfaEnrolment({
    onSuccess: (pending) => {
      setEnrolment(pending);
      setOpenDialog('enable');
    },
    onError: (error) => toast.error(error.error || 'Could not start the setup, try again'),
  });

  const closeDialog = (open: boolean) => {
    if (!open) {
      setOpenDialog(null);
      setEnrolment(null);
    }
  };

  const enabled = status?.enabled ?? user.mfaEnabled ?? false;
  const enabledAt = status?.enabledAt ?? user.mfaEnabledAt ?? null;
  const remaining = status?.recoveryCodesRemaining ?? null;
  // While required the API refuses to disable it (409), so the button is not offered at all.
  const required = status?.required ?? false;
  // `enabled` is known from the user right away, `required` only once the status is: until then
  // neither button acts, or "Disable" would be clickable for someone the API will refuse.
  const isStatusPending = isLoading && !status;

  return (
    <>
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl">Two-factor authentication</CardTitle>
          <CardDescription>
            Add a second step to sign-in: a code from an authenticator app on your phone, on top
            of your password or social login.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4 py-2">
            <div className="flex items-center gap-3">
              {enabled ? (
                <IconShieldCheck className="size-6 shrink-0 text-teal-600" />
              ) : (
                <IconShieldOff className="size-6 shrink-0 text-muted-foreground" />
              )}
              <div className="flex flex-col">
                <span className="font-semibold">Authenticator app</span>
                <span className="text-sm text-muted-foreground">
                  {enabled
                    ? enabledAt
                      ? `Enabled since ${format(new Date(enabledAt), 'dd MMM yyyy')}`
                      : 'Enabled'
                    : 'Not enabled'}
                </span>
              </div>
            </div>
            {enabled ? (
              <Badge variant="success">On</Badge>
            ) : (
              <Badge variant="outline">Off</Badge>
            )}
          </div>

          {enabled && remaining !== null && (
            <div className="flex items-center justify-between gap-4 border-t py-3">
              <div className="flex flex-col">
                <span className="text-sm font-medium">Recovery codes</span>
                <span className="text-sm text-muted-foreground">
                  {remaining === 0
                    ? 'None left, you cannot sign in without your app'
                    : `${remaining} unused ${remaining === 1 ? 'code' : 'codes'} to sign in without your app`}
                </span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpenDialog('regenerate')}>
                Regenerate
              </Button>
            </div>
          )}

          {enabled && remaining !== null && remaining < LOW_RECOVERY_CODES && (
            <Alert variant={remaining === 0 ? 'destructive' : 'important'}>
              <TriangleAlertIcon />
              <AlertTitle>
                {remaining === 0 ? 'No recovery codes left' : 'Running low on recovery codes'}
              </AlertTitle>
              <AlertDescription>
                Regenerate them now so that losing your phone does not lock you out of Tinct.
              </AlertDescription>
            </Alert>
          )}

          {required && (
            <div className="flex items-start gap-2 border-t pt-3 text-sm text-muted-foreground">
              <LockIcon className="mt-0.5 size-4 shrink-0" />
              <span>
                {describeRequirement(status?.requiredBy)}{' '}
                {enabled
                  ? 'It cannot be disabled.'
                  : 'Set it up now, you will be asked for it at your next sign-in.'}
              </span>
            </div>
          )}
        </CardContent>
        {/* Required and enabled leaves nothing to offer: "Disable" would only be refused */}
        {!(enabled && required) && (
          <CardFooter className="justify-end gap-2 border-t">
            {enabled ? (
              <Button
                type="button"
                variant="outline"
                className="text-destructive hover:text-destructive"
                disabled={isStatusPending}
                onClick={() => setOpenDialog('disable')}
              >
                Disable
              </Button>
            ) : (
              <Button
                type="button"
                isLoading={startEnrolment.isPending}
                disabled={isStatusPending}
                onClick={() => startEnrolment.mutate({ userId })}
              >
                Set up
              </Button>
            )}
          </CardFooter>
        )}
      </Card>

      {openDialog === 'enable' && enrolment && (
        <EnableMfaModal open onOpenChange={closeDialog} user={user} enrolment={enrolment} />
      )}
      {openDialog === 'disable' && <DisableMfaModal open onOpenChange={closeDialog} user={user} />}
      {openDialog === 'regenerate' && (
        <RegenerateRecoveryCodesModal open onOpenChange={closeDialog} user={user} />
      )}
    </>
  );
};
